import React, { useEffect, useRef, useState } from "react";
import Steps from "../../Steps";
import Button from "../../../../common/Button";
import ChartNav from "../../../../common/ChartNav";
import Medications from "../../medications/medications";
import { Link, useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  GeneralInformationTab,
  BloodPressureTab,
  LifeStyleTab,
  SmokingPage,
  RecreationalDrugAlcoholPage,
  RecreationDrugCard,
  AlcoholCard,
  EducationUITypes,
  ConditionReasons,
  CholesterolTab,
  AsthmaTab,
  HeartFailureTab,
  DiabetesTab,
} from "./data";
import { updateMedicationReview } from "../../../../../redux/modules/clinical-services/actions";
import PrescriptionTabs from "./prescription-tabs";
import EducationPage from "./edu-page";
import { ClinicalServiceTypes, getValidStep } from "../../../text/data";
import SubNavigation from "../../common/sub-navigation";
import EducationDialog from "./EductionDialog";
import ConfirmationModalNew from "../../../../common/ConfirmationModalNew";

const Informations = () => {
  const { clinicalServiceObj, clinicalServiceName, clinicalServiceSteps } = useSelector(
    (state) => state.clinicalService
  );
  const dispatch = useDispatch();
  const { serviceId } = useParams();
  const { currentStep, currentSubStep } =
    clinicalServiceObj.initialAssessment.initialAssessmentForm;
  const [activeTabIndex, setActiveTabIndex] = useState(null);
  const [totalTabs, setTotalTabs] = useState([]);
  const [activePage, setActivePage] = useState(1);
  const [completeSubStep, setCompleteSubStep] = useState(false);
  const [showSideMedication, setShowSideMedication] = useState(false);
  const [showEducationTabs, setShowEducationTabs] = useState(false);
  const [showEducationDialog, setShowEducationDialog] = useState(false);
  const [calculatedTabs, setCalculatedTabs] = useState([]);
  const [completedTabNames, setCompletedTabNames] = useState([]);
  const [medicationCreateRequest, setMedicationCreateRequest] = useState(null);
  const [allTabsCompleted, setAllTabsCompleted] = useState(false);
  const [showManageBPDialog, setShowManageBPDialog] = useState(false);
  const [hasShownBPDialogOnPage4, setHasShownBPDialogOnPage4] = useState(false);
  const [showManageCholDialog, setShowManageCholDialog] = useState(false);
  const [hasShownCholDialogOnPage4, setHasShownCholDialogOnPage4] = useState(false);
  const hasAutoSkippedEmptyEducationRef = useRef(false);

  const bpDependencies = [
    "How high are your Blood Pressure numbers?",
    "If blood pressure targets are not achieved"
  ];

  const cholesterolDependencies = [
    "How high are your cholesterol numbers?",
    "If cholesterol targets are not achieved"
  ];

  const normalizeReasonText = (value) =>
    String(value || "")
      .toLowerCase()
      .replace(/[^a-z0-9\s]/g, " ")
      .replace(/\s+/g, " ")
      .trim();

  const hasReasonMatch = (selectedReasons = [], expectedReasons = []) => {
    const normalizedSelected = selectedReasons
      .map((reason) => normalizeReasonText(reason))
      .filter(Boolean);
    if (!normalizedSelected.length) return false;

    return expectedReasons.some((expected) => {
      const normalizedExpected = normalizeReasonText(expected);
      if (!normalizedExpected) return false;
      return normalizedSelected.some(
        (selected) =>
          selected === normalizedExpected ||
          selected.includes(normalizedExpected) ||
          normalizedExpected.includes(selected)
      );
    });
  };

  useEffect(() => {
    let lifeStyleObj =
      clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis
        ? clinicalServiceObj.initialAssessment.initialAssessmentForm
            .clinicalAssessment
        : clinicalServiceObj.initialAssessment.initialAssessmentForm
            .healthAndWellness;
    let medicationObj =
      clinicalServiceName === ClinicalServiceTypes.AllergicRhinitis
        ? undefined
        : clinicalServiceObj.initialAssessment.initialAssessmentForm
            .medications;
    if (
      clinicalServiceObj.initialAssessment.initialAssessmentForm !==
        undefined &&
      lifeStyleObj !== null &&
      lifeStyleObj !== undefined &&
      medicationObj !== null &&
      medicationObj !== undefined
    ) {
      const nextTabs = calculateTabs(lifeStyleObj, medicationObj);
      if (nextTabs.length === 0) {
        setShowEducationDialog(false);
        setShowEducationTabs(false);
        setTotalTabs([]);
        setActiveTabIndex(null);
        setActivePage(1);
        if (!hasAutoSkippedEmptyEducationRef.current) {
          hasAutoSkippedEmptyEducationRef.current = true;
          onNext();
        }
        return;
      }

      hasAutoSkippedEmptyEducationRef.current = false;
      setShowEducationDialog(true);
    }
  }, [clinicalServiceObj]);

  // Auto-open Manage Blood Pressure dialog when reaching page 4 for the first time
  useEffect(() => {
    const currentTab = totalTabs[activeTabIndex];
    const isBloodPressureTab = currentTab?.name === BloodPressureTab.name;
    const isOnPage4 = activePage === 4;
    
    if (isBloodPressureTab && isOnPage4 && !hasShownBPDialogOnPage4) {
      setShowManageBPDialog(true);
      setHasShownBPDialogOnPage4(true);
    }
  }, [activePage, activeTabIndex, totalTabs, hasShownBPDialogOnPage4]);

  // Auto-open Manage Cholesterol dialog when reaching page 4 for the first time
  useEffect(() => {
    const currentTab = totalTabs[activeTabIndex];
    const isCholesterolTab = currentTab?.name === CholesterolTab.name;
    const isOnPage4 = activePage === 4;

    if (isCholesterolTab && isOnPage4 && !hasShownCholDialogOnPage4) {
      setShowManageCholDialog(true);
      setHasShownCholDialogOnPage4(true);
    }
  }, [activePage, activeTabIndex, totalTabs, hasShownCholDialogOnPage4]);

  // Reset the flag when changing tabs
  useEffect(() => {
    setHasShownBPDialogOnPage4(false);
    setHasShownCholDialogOnPage4(false);
  }, [activeTabIndex]);

  // Enable Next button when reaching the last page of the last tab
  useEffect(() => {
    if (totalTabs.length > 0 && activeTabIndex != null) {
      const currentTab = totalTabs[activeTabIndex];
      const isLastTab = activeTabIndex === totalTabs.length - 1;
      const isLastPageOfTab = currentTab?.steps && activePage >= currentTab.steps.length;
      setAllTabsCompleted(isLastTab && isLastPageOfTab);
    }
  }, [activePage, activeTabIndex, totalTabs]);

  const calculateTabs = (lifeStyleObj, medicationObj) => {
    const tabs = getTabs(medicationObj);
    const hasBloodPressureOrCholesterol =
      tabs.some((tab) => tab.name === BloodPressureTab.name) ||
      tabs.some((tab) => tab.name === CholesterolTab.name);

    // No education popup and no lifestyle tab when neither BP nor Cholesterol applies.
    if (!hasBloodPressureOrCholesterol) {
      setCalculatedTabs([]);
      return [];
    }

    const lsTab = getLifeStyleTab(lifeStyleObj);
    if (lsTab) tabs.push(lsTab);

    setCalculatedTabs(tabs);
    return tabs;
  };

  const getTabs = (medicationObj) => {
    const tabs = [];

    // Check if at least one item exists (some)
    if (medicationObj && medicationObj.medications.length > 0) {
      const reasons = medicationObj.medications.map(
        (x) => x.reasonForMedicationName
      );
      if (hasReasonMatch(reasons, ConditionReasons.bloodPressure))
        tabs.push(BloodPressureTab);
      if (hasReasonMatch(reasons, ConditionReasons.cholesterol))
        tabs.push(CholesterolTab);
      if (hasReasonMatch(reasons, ConditionReasons.diabetes))
        tabs.push(DiabetesTab);
      if (hasReasonMatch(reasons, ConditionReasons.heartFailure))
        tabs.push(HeartFailureTab);
      if (hasReasonMatch(reasons, ConditionReasons.asthama))
        tabs.push(AsthmaTab);
    }

    return tabs;
  };

  const getLifeStyleTab = (obj) => {
    // Create a deep copy to avoid mutating the original LifeStyleTab object
    const lifeStyle = {
      ...LifeStyleTab,
      steps: [...LifeStyleTab.steps]
    };
    if (
      obj?.smoking !== "Never" &&
      !lifeStyle.steps.some((x) => x.name === SmokingPage.name)
    )
      lifeStyle.steps.push(SmokingPage);

    const alcoholPage = { ...RecreationalDrugAlcoholPage };
    const cards = [];
    if (obj?.alcohol !== "Never") cards.push(AlcoholCard);
    if (
      !obj?.recreationalDrugs.includes("None") &&
      obj?.recreationalDrugs.length > 0
    )
      cards.push(RecreationDrugCard);
    if (
      cards.length > 0 &&
      !lifeStyle.steps.some((x) => x.name === alcoholPage.name)
    ) {
      alcoholPage.cards = cards;
      if (cards.length > 1)
        alcoholPage.title =
          "Lifestyle Management - Alcohol and Recreational Drugs";
      else {
        alcoholPage.title =
          cards[0].title === "Alcohol"
            ? "Lifestyle Management - Alcohol"
            : "Lifestyle Management - Recreational Drugs";
      }
      lifeStyle.steps.push(alcoholPage);
    }
    return lifeStyle;
  };

  const setTabs = (tabs) => {
    setTotalTabs(tabs);
    setActiveTabIndex(0);
    setActivePage(1);
    setCompletedTabNames([]);
  };

  const onNext = () => {
    if (currentStep <= clinicalServiceSteps.length) {
      const { nextStep, nextSubStep } = getValidStep(clinicalServiceSteps, currentStep, currentSubStep, 1);
      const updatedSteps = {
        currentStep: nextStep,
        currentSubStep: nextSubStep,
        serviceSteps: clinicalServiceSteps
      };
      dispatch(updateMedicationReview(serviceId, updatedSteps));
    }
  };

  const onPrevious = () => {
    const { nextStep, nextSubStep } = getValidStep(clinicalServiceSteps, currentStep, currentSubStep, -1);
    const updatedSteps = { currentStep: nextStep, currentSubStep: nextSubStep };
    dispatch(updateMedicationReview(serviceId, updatedSteps));
  };

  const moveToPreviousPage = () => {
    if (activePage !== 1) {
      const currentTab = totalTabs[activeTabIndex];
      const isBloodPressureTab = currentTab?.name === BloodPressureTab.name;
      // Skip page 3 only for Blood Pressure tab - if on page 4, go directly to page 2
      if (isBloodPressureTab && activePage === 4) {
        setActivePage(2);
      } else {
        setActivePage(activePage - 1);
      }
    }
  };

  const moveToNextPage = () => {
    const currentTab = totalTabs[activeTabIndex];
    if (currentTab?.steps && activePage !== currentTab.steps.length) {
      const isBloodPressureTab = currentTab?.name === BloodPressureTab.name;
      // Skip page 3 only for Blood Pressure tab - if on page 2, go directly to page 4
      if (isBloodPressureTab && activePage === 2) {
        setActivePage(4);
      } else {
        setActivePage(activePage + 1);
      }
    }
  };

  const moveToNextTab = (tabs = []) => {
    const tabArr = tabs.length === 0 ? totalTabs : tabs;
    if (!tabArr || tabArr.length === 0) return;
    const currentTabName = tabArr[activeTabIndex]?.name;
    if (currentTabName) {
      setCompletedTabNames((prev) =>
        prev.includes(currentTabName) ? prev : [...prev, currentTabName]
      );
    }
    
    if (activeTabIndex < tabArr.length - 1) {
      setActiveTabIndex(activeTabIndex + 1);
      setActivePage(1);
    } else {
      // Check if we're on the last page of the last tab (specifically the Lifestyle tab)
      const currentTab = tabArr[activeTabIndex];
      if (!currentTab || !currentTab.steps) {
        setCompleteSubStep(true);
        return;
      }
      
      const isLastTab = activeTabIndex === tabArr.length - 1;
      const isLastPageOfTab = activePage === currentTab.steps.length;
      const isLifestyleTab = currentTab.name === LifeStyleTab.name;
      
      if (isLastTab && isLastPageOfTab && isLifestyleTab) {
        setAllTabsCompleted(true);
      }
      setCompleteSubStep(true);
    }
  };

  const UpdateShowMedication = (val) => {
    setShowSideMedication(val);
  };

  const ConfirmEducation = () => {
    setShowEducationDialog(true);
    onNext();
  };

  return (
    <div className="relative flex flex-col flex-1 w-full gap-4 bg-white">
      <div className="flex flex-col flex-1">
        <div className="flex flex-1 gap-4">
          <ChartNav />
          <Medications
            showMedication={showSideMedication}
            isAMR={false}
            selectMedication={null}
            highlightAmrEducationMatches
            medicationCreateRequest={medicationCreateRequest}
          />
          <div className="flex flex-1 w-full h-[668px] overflow-y-auto scroll-smooth ">
            <div className="w-full gap-5">
              <div className="flex items-center justify-between mt-0.5 mb-0.5">
                <h4 className="text-xs-display font-bold text-teal-300">
                  Education - Review Prescriptions
                </h4>

                <SubNavigation />
              </div>
              <div className="border-b-[7px] border-gray-20 mb-[20px]"></div>
              {activeTabIndex >= 0 && (
                <div className="flex flex-col ">
                  <div className="flex items-center ">
                    <PrescriptionTabs
                      activeTab={totalTabs[activeTabIndex] || null}
                      setActiveTab={(tab) => {
                        setActiveTabIndex(
                          totalTabs.findIndex((x) => x.name === tab.name)
                        );
                        setActivePage(1);
                      }}
                      tabs={totalTabs}
                      completedTabs={completedTabNames}
                    />
                    {totalTabs[activeTabIndex]?.steps?.[activePage - 1]?.type === EducationUITypes.DrugTable && (
                        <div className="ml-auto pr-20">
                          {totalTabs[activeTabIndex]?.name === BloodPressureTab.name && (
                            <Button
                              type="primary"
                              text="Manage Blood Pressure"
                              className="whitespace-nowrap !min-w-[220px]"
                              onClick={() => setShowManageBPDialog(true)}
                            />
                          )}
                          {totalTabs[activeTabIndex]?.name === CholesterolTab.name && (
                            <Button
                              type="primary"
                              text="Manage Cholesterol"
                              className="whitespace-nowrap !min-w-[220px] bg-green-300"
                              onClick={() => setShowManageCholDialog(true)}
                            />
                          )}
                        </div>
                    )}
                  </div>
                  <div className="gap-6 flex flex-row">
                    {(() => {
                      const currentTab = totalTabs[activeTabIndex];
                      const steps = currentTab?.steps || [];
                      const stepIndex = activePage - 1;
                      const finalStepIndex = stepIndex >= steps.length ? steps.length - 1 : stepIndex;
                      const stepToRender = steps[finalStepIndex];
                      
                      if (!stepToRender) return null;
                      
                      const commonProps = {
                        totalTabs,
                        activePage,
                        tabIndex: activeTabIndex,
                        moveBack: moveToPreviousPage,
                        moveForward: moveToNextPage,
                        moveToNextTab,
                        updateShowMedication: UpdateShowMedication,
                        confirmEducation: ConfirmEducation,
                        onSelectDrugForMedicationCreate: (drugName) => {
                          setMedicationCreateRequest({
                            drugName,
                            requestId: Date.now(),
                          });
                        },
                      };
                      
                      return (
                        <div className="w-[2200px] pl-4 pt-4">
                          <EducationPage step={stepToRender} {...commonProps} />
                        </div>
                      );
                    })()}
                  </div>
                  <div className="flex flex-row p-2 justify-center">
                    {totalTabs[activeTabIndex]?.steps.map(
                      (subStep, subIndex) => {
                        return (
                          <div
                            key={subIndex}
                            className={`h-[10px] m-1 w-[10px] rounded-full ${
                              activePage === subIndex + 1
                                ? "bg-REELTeal"
                                : "bg-gray-200"
                            }`}
                          />
                        );
                      }
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between space-x-4 rounded">
        <div className="w-full ">
          <Steps position="bottom" stepsList={clinicalServiceSteps} />
        </div>
        <div className="flex items-center gap-3 ">
          <Button
            type="primary-outline"
            text="Back"
            className="w-[200px] max-h-[52px] !rounded-[30px]"
            onClick={() => {
              onPrevious();
            }}
          />
          <Button
            type="primary"
            text="Next"
            className={`w-[200px] max-h-[52px] !rounded-[30px]`}
            disabled={calculatedTabs.length > 0 && !allTabsCompleted}
            onClick={() => {
              onNext();
            }}
          />
        </div>
      </div>

      <EducationDialog
        open={showEducationDialog}
        className="font-semibold text-gray-800"
        handleClose={() => {
          setShowEducationDialog(false);
          onNext();
        }}
        onCross={() => setShowEducationDialog(false)}
        confirmAction={() => {
          setShowEducationDialog(false);
          setShowEducationTabs(true);
          setTabs(calculatedTabs);
          setActiveTabIndex(0);
          setActivePage(1);
        }}
      />

      {/* Manage Blood Pressure Dialog */}
      <ConfirmationModalNew
        open={showManageBPDialog}
        title="Manage Blood Pressure"
        className="!w-[530px]"
        content={() => (
          <div className="flex flex-col gap-6">
            <p className="text-lg font-semibold text-gray-700">
              Number of drugs to treat Blood Pressure depends on:
            </p>
            <ul className="flex flex-col gap-3">
              {bpDependencies.map((item, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <span className="w-2 h-2 rounded-full bg-teal-300 mt-2 flex-shrink-0"></span>
                  <span className="text-base text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        type="primary"
        confirmText="Close"
        cancelText={null}
        handleClose={() => setShowManageBPDialog(false)}
        onCross={() => setShowManageBPDialog(false)}
        confirmAction={() => setShowManageBPDialog(false)}
        hideActions={false}
        maskClosable={true}
        actionsClassName="!flex !justify-center"
        buttonClassName="!min-w-[240px]"
      />

      {/* Manage Cholesterol Dialog */}
      <ConfirmationModalNew
        open={showManageCholDialog}
        title="Manage Cholesterol"
        className="!w-[520px]"
        content={() => (
          <div className="flex flex-col gap-6">
            <p className="text-base font-semibold text-gray-700">Number of drugs to manage Cholesterol depends on:</p>
            <ul className="flex flex-col gap-3">
              {cholesterolDependencies.map((item, idx) => (
                <li key={idx} className="flex items-start gap-3">
                  <span className="w-2 h-2 rounded-full bg-teal-300 mt-2 flex-shrink-0"></span>
                  <span className="text-base text-gray-700">{item}</span>
                </li>
              ))}
            </ul>
          </div>
        )}
        type="primary"
        confirmText="Close"
        cancelText={null}
        handleClose={() => setShowManageCholDialog(false)}
        onCross={() => setShowManageCholDialog(false)}
        confirmAction={() => setShowManageCholDialog(false)}
        hideActions={false}
        maskClosable={true}
        actionsClassName="!flex !justify-center"
        buttonClassName="!min-w-[240px] bg-teal-300"
        closeClassName="bg-teal-50"
      />
    </div>
  );
};

export default Informations;